import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SvgIcon } from '@progress/kendo-react-common';
import { chevronRightIcon, copyIcon, infoCircleIcon, trashIcon } from '@progress/kendo-svg-icons';
import { DlpProjectModel, DlpQuotaProfileRowModel, DlpScopeModel } from 'services/DlpConfiguration/DlpConfigurationService';
import { DlpIssue } from 'utils/dlpConfigurationValidation';
import { IssueList } from '../Common/IssueList';
import { SaveBar } from '../Common/SaveBar';
import { ConfirmDialog } from '../Common/ConfirmDialog';
import { EditableTitle } from '../Common/EditableTitle';
import { useReportDirty } from '../Common/useUnsavedChangesGuard';
import { ProfileRowsEditor } from './ProfileRowsEditor';

interface ProfileDetailProps {
    project: DlpProjectModel;
    name: string;
    rows: DlpQuotaProfileRowModel[];
    // Other profile names on the project, to block rename collisions
    otherNames: string[];
    referencingScopes: DlpScopeModel[];
    issues: DlpIssue[];
    onSave: (currentName: string, newName: string, rows: DlpQuotaProfileRowModel[]) => Promise<void>;
    onDuplicate: (name: string) => Promise<void>;
    onDelete: (name: string) => Promise<void>;
    onOpenScope: (scopeId: number) => void;
    onDirtyChange: (dirty: boolean) => void;
}

// Mount with key={name}: the draft is initialised once per profile.
export function ProfileDetail({ project, name, rows, otherNames, referencingScopes, issues, onSave, onDuplicate, onDelete, onOpenScope, onDirtyChange }: ProfileDetailProps) {
    const { t } = useTranslation();
    const [draftName, setDraftName] = useState(name);
    const [draftRows, setDraftRows] = useState<DlpQuotaProfileRowModel[]>(rows);
    const [scopesOpen, setScopesOpen] = useState(false);
    const [confirm, setConfirm] = useState<'save' | 'delete' | null>(null);

    // A profile used by scopes is read-only, so those scopes' quotas can't shift under them.
    const locked = referencingScopes.length > 0;
    const trimmedName = draftName.trim();
    const nameCollision = trimmedName !== name && otherNames.includes(trimmedName);
    const isDirty = trimmedName !== name || JSON.stringify(draftRows) !== JSON.stringify(rows);
    useReportDirty(isDirty, onDirtyChange);

    const save = async () => {
        setConfirm(null);
        await onSave(name, trimmedName || name, draftRows);
    };

    const remove = async () => {
        setConfirm(null);
        await onDelete(name);
    };

    return (
        <div className="detail-col">
            <div className="detail-header">
                <div style={{ flex: 1 }}>
                    <EditableTitle
                        value={draftName}
                        onChange={setDraftName}
                        onCancel={() => setDraftName(name)}
                        placeholder={t('dlpConfiguration.profiles.namePlaceholder')}
                        error={nameCollision ? t('dlpConfiguration.profiles.nameCollision', { profile: trimmedName }) : undefined}
                    />
                    <div className="detail-sub">{t('dlpConfiguration.profiles.belongsTo', { project: project.id })}</div>
                </div>
                <div className="detail-actions">
                    <button type="button" className="lm-btn-secondary sm" onClick={() => setScopesOpen(o => !o)}>
                        <SvgIcon icon={chevronRightIcon} size="small" className={scopesOpen ? 'rotate-90' : ''} />
                        {t('dlpConfiguration.profiles.scopeCount', { count: referencingScopes.length })}
                    </button>
                    {locked && (
                        <button type="button" className="lm-btn-secondary sm" onClick={() => onDuplicate(name)}>
                            <SvgIcon icon={copyIcon} size="small" /> {t('dlpConfiguration.profiles.duplicate')}
                        </button>
                    )}
                    <button
                        type="button"
                        className="lm-btn-danger sm"
                        disabled={locked}
                        title={locked ? t('dlpConfiguration.profiles.deleteLocked') : undefined}
                        onClick={() => setConfirm('delete')}
                    >
                        <SvgIcon icon={trashIcon} size="small" /> {t('dlpConfiguration.profiles.deleteProfile')}
                    </button>
                </div>
            </div>

            {scopesOpen && (
                <div className="scopes-using-list">
                    {referencingScopes.length === 0 && <div className="empty-note">{t('dlpConfiguration.profiles.noScopes')}</div>}
                    {referencingScopes.map(s => (
                        <button key={s.scope_id} type="button" className="scope-using-row" onClick={() => onOpenScope(s.scope_id)}>
                            <span className="scope-using-brand">{s.name}</span>
                            <SvgIcon icon={chevronRightIcon} size="small" className="chevron" />
                        </button>
                    ))}
                </div>
            )}

            <IssueList issues={issues} />

            {locked && (
                <div className="hint-block locked-hint">
                    <SvgIcon icon={infoCircleIcon} size="small" />
                    {t('dlpConfiguration.profiles.lockedHint', { count: referencingScopes.length })}
                </div>
            )}

            <div className="field-label" style={{ marginTop: 4 }}>{t('dlpConfiguration.profiles.rows')}</div>
            {!locked && (
                <div className="hint-block">
                    <SvgIcon icon={infoCircleIcon} size="small" />
                    {t(project.group_by.includes('label') ? 'dlpConfiguration.profiles.rowsHintLabel' : 'dlpConfiguration.profiles.rowsHint')}
                </div>
            )}
            <ProfileRowsEditor project={project} rows={draftRows} onChange={setDraftRows} disabled={locked} />

            {/* When locked only a rename can make it dirty, and a rename carries the scopes along */}
            {isDirty && <SaveBar onClick={() => setConfirm('save')} disabled={nameCollision} />}
            {confirm === 'save' && (
                <ConfirmDialog
                    title={t('dlpConfiguration.profiles.saveTitle')}
                    message={t('dlpConfiguration.profiles.saveMessage', { profile: trimmedName || name })}
                    confirmLabel={t('general.save')}
                    onConfirm={save}
                    onCancel={() => setConfirm(null)}
                />
            )}
            {confirm === 'delete' && (
                <ConfirmDialog
                    title={t('dlpConfiguration.profiles.deleteTitle')}
                    message={t('dlpConfiguration.profiles.deleteMessage', { profile: name })}
                    confirmLabel={t('general.delete')}
                    onConfirm={remove}
                    onCancel={() => setConfirm(null)}
                    danger
                />
            )}
        </div>
    );
}
